'use client'

import { useState, useEffect, useSyncExternalStore } from 'react'
import dynamic from 'next/dynamic'
import { alpha, Box, Typography } from '@mui/material'

// Types
import type { InternetStatusProps } from '@/types/components/atoms/item/internet-status'

// Helpers
import { fetchCheckOnline } from '@/helpers/api/check-online'

// Components
const CloudConnectAtom = dynamic(() => import('@/components/atoms/icon/two-tone/cloud-connect'))
const CloudDisconnectAtom = dynamic(() => import('@/components/atoms/icon/two-tone/cloud-disconnect'))

const subscribeNavigatorOnline = (onStoreChange: () => void) => {
	window.addEventListener('online', onStoreChange)
	window.addEventListener('offline', onStoreChange)

	return () => {
		window.removeEventListener('online', onStoreChange)
		window.removeEventListener('offline', onStoreChange)
	}
}

const getNavigatorOnlineSnapshot = () => navigator.onLine
const getNavigatorOnlineServerSnapshot = () => true

const InternetStatus = (props: InternetStatusProps) => {
	// Props
	const { onlineText, offlineText } = props

	// Variables
	const navigatorOnline = useSyncExternalStore(subscribeNavigatorOnline, getNavigatorOnlineSnapshot, getNavigatorOnlineServerSnapshot)
	const [googleOnline, setGoogleOnline] = useState<boolean>(false)

	// Callbacks
	useEffect(() => {
		if (!navigatorOnline) return

		let cancelled = false
		let timeout: number
		let delay = 1000
		let lastStatus: boolean | null = null

		const schedule = () => {
			timeout = window.setTimeout(refresh, delay)
		}

		const refresh = () => {
			fetchCheckOnline().then(isOnline => {
				if (cancelled) return

				setGoogleOnline(isOnline)

				if (lastStatus === null) {
					delay = 1000
				} else if (isOnline !== lastStatus) {
					delay = 1000
				} else {
					delay = Math.min(delay * 2, 64000)
				}

				lastStatus = isOnline

				schedule()
			})
		}

		refresh()

		return () => {
			cancelled = true
			window.clearTimeout(timeout)
		}
	}, [navigatorOnline])

	const isOnline = navigatorOnline && googleOnline

	return (
		<Box
			sx={{
				position: 'fixed',
				right: 10,
				bottom: 10,
				fontSize: 14,
				color: isOnline ? 'green' : 'red',
				borderRadius: 1,
				zIndex: 'calc(var(--mui-zIndex-tooltip) + 1)',
				'& .MuiSvgIcon-root': { fontSize: 32 }
			}}
		>
			{isOnline ? (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 1,
						p: 1,
						borderRadius: 1,
						bgcolor: alpha('#008c0e', 0.1),
						animation: 'backOut 1s 4s forwards ',
						'@keyframes backOut': {
							'0%': { bgcolor: alpha('#008c0e', 0.1), backdropFilter: 'blur(5px)' },
							'100%': { bgcolor: 'transparent', backdropFilter: 'blur(0px)' }
						}
					}}
				>
					<CloudConnectAtom />

					<Typography
						sx={{
							fontSize: 14,
							animation: 'fadeOut 1s 4s forwards ',
							'@keyframes fadeOut': {
								'0%': { opacity: 1, height: 'auto', width: 'auto' },
								'10%': { opacity: 0.5, height: 'auto', width: 'auto' },
								'100%': { opacity: 0, height: 0, width: 0 }
							}
						}}
					>
						{onlineText}
					</Typography>
				</Box>
			) : (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 1,
						bgcolor: alpha('#ff0000', 0.1),
						borderRadius: 1,
						p: 1,
						backdropFilter: 'blur(5px)'
					}}
				>
					<CloudDisconnectAtom />
					<Typography sx={{ fontSize: 14 }}>{offlineText}</Typography>
				</Box>
			)}
		</Box>
	)
}

export default InternetStatus
